import { Button, Form, Input, Space } from "antd"
import { motion } from "motion/react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import styled from "styled-components"

import { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import DisabledFieldTooltip from "#blocks/Form/DisabledFieldTooltip"
import { CLASS_GROUPS } from "#constants/classOptions"
import {
  BASIC_SKILL_LEVELS,
  DISCOUNT_TYPES,
  GREEN_LIME_SKILL_LEVELS,
  hasMaterialsFee,
  hasSuppliesFee,
  isPortfolioTuition,
  SCULPTURE_MATERIALS_FEE_PER_SESSION,
  SESSION_THRESHOLD_FOR_SEMESTER_RATE,
  shouldUseSemesterRate,
  SUPPLIES_FEES,
  type SuppliesFeeId,
  TUITION_RATES,
  YELLOW_ORANGE_RED_SKILL_LEVELS,
} from "#constants/feeConstants"
import {
  type CoreSkillLevel,
  type DigitalSkillLevel,
} from "#constants/skillLevelConstants"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import {
  calculateFeeBreakdown,
  type FeeBreakdown,
  getDefaultSuppliesFee,
} from "#utils/feeCalculationHelpers"

const SectionTitle = styled.h3`
  font-weight: 600;
  font-size: 14px;
  margin: 12px 0 4px 0;
`

const LedgerRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 32px;
  margin-bottom: 8px;
  padding-bottom: 8px;
`

const TotalSection = styled.div<{ $bgColor: string }>`
  background-color: ${({ $bgColor }) => $bgColor};
  border-radius: 6px;
  padding: 10px 12px;
  margin-top: 12px;
  margin-bottom: 12px;
`

const TotalRow = styled.div<{ $isBold?: boolean }>`
  display: flex;
  justify-content: space-between;
  font-weight: ${({ $isBold }) => ($isBold ? 600 : 400)};
  font-size: ${({ $isBold }) => ($isBold ? "14px" : "13px")};
  margin-bottom: ${({ $isBold }) => ($isBold ? 0 : "4px")};
`

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e0e0e0;
  margin: 8px 0;
`

const SelectableButton = styled(Button)<{
  $isSelected: boolean
  $selectedColor: string
}>`
  border-color: ${({ $isSelected, $selectedColor }) =>
    $isSelected ? $selectedColor : undefined};
  background-color: ${({ $isSelected, $selectedColor }) =>
    $isSelected ? `${$selectedColor}20` : undefined};

  &:hover:not(:disabled) {
    border-color: ${({ $selectedColor }) => $selectedColor} !important;
  }
`

const StyledInput = styled(Input)`
  width: 80px;
  // Add specificity with &&&
  &&&& .ant-input {
    text-align: right;
    /* padding-right: 10px; */
  }
`

// Abbreviated supplies fee labels
const SUPPLIES_FEE_LABELS: Record<SuppliesFeeId, string> = {
  coreArtBasicSupplies: "BG/P/BB/B",
  coreArtGreenLimeSupplies: "G/L",
  coreArtYellowOrangeRedSupplies: "Y/O/R",
  sketchingSupplies: "Sketch",
  collegePortfolioSupplies: "Portfolio",
}

interface FeeSelectorProps {
  classGroup: CLASS_GROUPS
  skillLevel: string | null | undefined
  curriculumSkillLevels: (CoreSkillLevel | DigitalSkillLevel)[]
  sessionCount: number
  netSessionCount: number
  totalSessionsRemaining: number
  onFeeBreakdownChange?: (breakdown: FeeBreakdown) => void
}

const FeeSelector = ({
  classGroup,
  skillLevel,
  curriculumSkillLevels,
  sessionCount,
  netSessionCount,
  totalSessionsRemaining,
  onFeeBreakdownChange,
}: FeeSelectorProps) => {
  const theme = useThemeContext()
  const form = Form.useFormInstance()

  // Watch form values (for inputs that have Form.Item)
  const tuitionOverride = Form.useWatch<number | null>("tuitionOverride", form)
  const discountOverride = Form.useWatch<number | null>(
    "discountOverride",
    form
  )
  const suppliesOverride = Form.useWatch<number | null>(
    "suppliesOverride",
    form
  )
  const materialsOverride = Form.useWatch<number | null>(
    "materialsOverride",
    form
  )

  // Local state for selections (form fields aren't registered without Form.Item)
  const [userSelectedRate, setUserSelectedRate] = useState<
    "semester" | "monthly" | null
  >(null)
  const [selectedDiscount, setSelectedDiscount] = useState<
    "sibling" | "extraClass" | "none"
  >("none")
  const [selectedSuppliesFeeId, setSelectedSuppliesFeeId] =
    useState<SuppliesFeeId | null>(null)
  const [waiveSupplies, setWaiveSupplies] = useState(false)

  // Determine discount type from local state
  const discountType = useMemo(() => {
    if (selectedDiscount === "sibling") return DISCOUNT_TYPES.SIBLING
    if (selectedDiscount === "extraClass") return DISCOUNT_TYPES.EXTRA_CLASS
    return null
  }, [selectedDiscount])

  // Calculate tuition rates
  const isPortfolio = isPortfolioTuition(classGroup)
  const rates = isPortfolio ? TUITION_RATES.portfolio : TUITION_RATES.standard
  const hasMonthlyRate = "monthly" in rates && rates.monthly != null
  const hasSemesterRate = "semester" in rates && rates.semester != null

  // Determine rate eligibility
  const canUseSemesterRate = shouldUseSemesterRate(
    netSessionCount,
    totalSessionsRemaining
  )
  const semesterRateDisabledReason = !canUseSemesterRate
    ? `Requires ${SESSION_THRESHOLD_FOR_SEMESTER_RATE}+ sessions (or all remaining if fewer than ${SESSION_THRESHOLD_FOR_SEMESTER_RATE} left)`
    : undefined

  // Determine which rate to use
  const usingSemesterRate = useMemo(() => {
    // If only semester rate exists, always use it
    if (!hasMonthlyRate && hasSemesterRate) return true
    // If only monthly rate is available (semester disabled), use monthly
    if (hasMonthlyRate && (!hasSemesterRate || !canUseSemesterRate)) {
      return false
    }
    // Otherwise, follow user selection or default to semester if eligible
    if (userSelectedRate === "monthly") return false
    if (userSelectedRate === "semester" && canUseSemesterRate) return true
    return canUseSemesterRate
  }, [userSelectedRate, canUseSemesterRate, hasMonthlyRate, hasSemesterRate])

  // Reset user selection if it becomes invalid
  useEffect(() => {
    if (userSelectedRate === "semester" && !canUseSemesterRate) {
      setUserSelectedRate(null)
    }
  }, [canUseSemesterRate, userSelectedRate])

  // Billable sessions = only charge for NEW sessions (not already enrolled)
  // Clamp to 0 - we don't refund for dropped sessions here
  const billableSessions = Math.max(0, netSessionCount)

  // Calculate fee breakdown with the selected rate
  // Use sessionCount for rate determination, billableSessions for the charge
  const feeBreakdown = useMemo(() => {
    const monthlyRate = "monthly" in rates ? rates.monthly : null
    const rate = usingSemesterRate
      ? rates.semester
      : (monthlyRate ?? rates.semester)
    const tuitionTotal = rate * billableSessions
    const discountedTuition = discountType
      ? Math.ceil(tuitionTotal * 0.9)
      : tuitionTotal
    const discountAmount = tuitionTotal - discountedTuition

    // Get supplies and materials from base calculation
    const baseBreakdown = calculateFeeBreakdown(
      classGroup,
      billableSessions,
      totalSessionsRemaining,
      skillLevel,
      discountType,
      waiveSupplies
    )

    return {
      ...baseBreakdown,
      tuitionPerSession: rate,
      tuitionTotal,
      tuitionAfterDiscount: discountedTuition,
      discountAmount,
      usingSemesterRate,
      sessionCount: billableSessions,
      grandTotal:
        discountedTuition +
        baseBreakdown.suppliesFee +
        baseBreakdown.materialsFee,
    }
  }, [
    classGroup,
    billableSessions,
    totalSessionsRemaining,
    skillLevel,
    discountType,
    waiveSupplies,
    usingSemesterRate,
    rates,
  ])

  // Apply overrides to breakdown
  // Explicitly zero supplies/materials for class groups that don't support them
  const groupHasSupplies = hasSuppliesFee(classGroup)
  const groupHasMaterials = hasMaterialsFee(classGroup)

  const finalBreakdown = useMemo((): FeeBreakdown => {
    const tuition = Number(tuitionOverride ?? feeBreakdown.tuitionTotal)
    const discount = Number(discountOverride ?? feeBreakdown.discountAmount)
    const supplies = groupHasSupplies
      ? waiveSupplies
        ? 0
        : Number(suppliesOverride ?? feeBreakdown.suppliesFee)
      : 0
    const materials = groupHasMaterials
      ? Number(materialsOverride ?? feeBreakdown.materialsFee)
      : 0

    return {
      ...feeBreakdown,
      tuitionTotal: tuition,
      tuitionAfterDiscount: tuition - discount,
      discountAmount: discount,
      suppliesFee: supplies,
      materialsFee: materials,
      grandTotal: tuition - discount + supplies + materials,
    }
  }, [
    feeBreakdown,
    tuitionOverride,
    discountOverride,
    suppliesOverride,
    materialsOverride,
    waiveSupplies,
    groupHasSupplies,
    groupHasMaterials,
  ])

  // Notify parent of fee breakdown changes
  useEffect(() => {
    onFeeBreakdownChange?.(finalBreakdown)
  }, [finalBreakdown, onFeeBreakdownChange])

  // Supplies fee options based on curriculum's allowed skill levels
  // (Moved up so it can be used by the default supplies fee effect)
  const suppliesFeeOptions = useMemo(() => {
    if (classGroup === CLASS_GROUPS.PORTFOLIO) {
      return [SUPPLIES_FEES.collegePortfolioSupplies]
    }
    if (
      classGroup === CLASS_GROUPS.CORE ||
      classGroup === CLASS_GROUPS.PAINTING
    ) {
      const allCoreOptions = [
        SUPPLIES_FEES.coreArtBasicSupplies,
        SUPPLIES_FEES.coreArtGreenLimeSupplies,
        SUPPLIES_FEES.coreArtYellowOrangeRedSupplies,
      ]

      // If curriculum skill levels not filled out, show all options
      if (!curriculumSkillLevels || curriculumSkillLevels.length === 0) {
        return allCoreOptions
      }

      const options: (typeof SUPPLIES_FEES)[keyof typeof SUPPLIES_FEES][] = []

      // Check if any curriculum skill levels match each supplies tier
      const hasBasic = curriculumSkillLevels.some((level) =>
        BASIC_SKILL_LEVELS.includes(level as CoreSkillLevel)
      )
      const hasGreenLime = curriculumSkillLevels.some((level) =>
        GREEN_LIME_SKILL_LEVELS.includes(level as CoreSkillLevel)
      )
      const hasYellowOrangeRed = curriculumSkillLevels.some((level) =>
        YELLOW_ORANGE_RED_SKILL_LEVELS.includes(level as CoreSkillLevel)
      )

      if (hasBasic) options.push(SUPPLIES_FEES.coreArtBasicSupplies)
      if (hasGreenLime) options.push(SUPPLIES_FEES.coreArtGreenLimeSupplies)
      if (hasYellowOrangeRed)
        options.push(SUPPLIES_FEES.coreArtYellowOrangeRedSupplies)

      // If no matches (shouldn't happen), show all options
      return options.length === 0 ? allCoreOptions : options
    }
    return []
  }, [classGroup, curriculumSkillLevels])

  // Set default supplies fee based on class group and skill level
  // If only one option exists, auto-select it regardless of skill level
  useEffect(() => {
    if (!hasSuppliesFee(classGroup)) {
      setSelectedSuppliesFeeId(null)
      return
    }

    // If only one supplies option, always select it
    if (suppliesFeeOptions.length === 1) {
      setSelectedSuppliesFeeId(suppliesFeeOptions[0].id as SuppliesFeeId)
      return
    }

    const { feeId } = getDefaultSuppliesFee(classGroup, skillLevel)
    setSelectedSuppliesFeeId(feeId)
  }, [classGroup, skillLevel, suppliesFeeOptions])

  // Initialize form values ONCE when we have valid calculated values
  // This runs once when feeBreakdown first calculates with real data
  const hasInitialized = useRef(false)
  useEffect(() => {
    // Only initialize once, and only when we have a meaningful tuition value
    if (hasInitialized.current) return
    if (feeBreakdown.tuitionTotal === 0 && sessionCount > 0) return // Wait for real calculation

    hasInitialized.current = true

    // Set all initial values at once
    form.setFieldsValue({
      tuitionOverride: feeBreakdown.tuitionTotal,
      discountOverride: feeBreakdown.discountAmount,
      suppliesOverride: feeBreakdown.suppliesFee,
      materialsOverride: feeBreakdown.materialsFee,
      siblingDiscount: false,
      extraClassDiscount: false,
    })
  }, [feeBreakdown, form, sessionCount])

  // Sync tuition/materials overrides when billable session count changes
  const prevBillableRef = useRef(billableSessions)
  useEffect(() => {
    if (!hasInitialized.current) return
    if (prevBillableRef.current === billableSessions) return
    prevBillableRef.current = billableSessions

    // Recalculate tuition: rate × new session count
    const monthlyRate = "monthly" in rates ? rates.monthly : null
    const rate = usingSemesterRate
      ? rates.semester
      : (monthlyRate ?? rates.semester)
    const newTuition = rate * billableSessions
    form.setFieldValue("tuitionOverride", newTuition)

    // Recalculate discount if one is applied
    if (selectedDiscount !== "none") {
      const newDiscount = Math.ceil(newTuition * 0.1)
      form.setFieldValue("discountOverride", newDiscount)
    }

    // Recalculate materials for sculpture
    if (hasMaterialsFee(classGroup)) {
      form.setFieldValue(
        "materialsOverride",
        SCULPTURE_MATERIALS_FEE_PER_SESSION * billableSessions
      )
    }
  }, [
    billableSessions,
    rates,
    usingSemesterRate,
    form,
    selectedDiscount,
    classGroup,
  ])

  // Handle rate selection - update form with new tuition
  const handleRateSelect = useCallback(
    (rateType: "semester" | "monthly") => {
      setUserSelectedRate(rateType)
      const monthlyRate = "monthly" in rates ? rates.monthly : null
      const rate =
        rateType === "semester"
          ? rates.semester
          : (monthlyRate ?? rates.semester)
      const newTuition = rate * sessionCount
      form.setFieldValue("tuitionOverride", newTuition)

      // Recalculate discount if one is selected
      if (selectedDiscount !== "none") {
        const discountAmount = Math.ceil(newTuition * 0.1)
        form.setFieldValue("discountOverride", discountAmount)
      }
    },
    [rates, sessionCount, form, selectedDiscount]
  )

  // Handle supplies fee selection
  const handleSuppliesFeeSelect = useCallback(
    (feeId: SuppliesFeeId) => {
      setSelectedSuppliesFeeId(feeId)
      setWaiveSupplies(false)
      form.setFieldValue("suppliesOverride", SUPPLIES_FEES[feeId].price)
    },
    [form]
  )

  // Handle discount selection (toggle behavior - click again to deselect)
  const handleDiscountSelect = useCallback(
    (type: "sibling" | "extraClass") => {
      const newDiscount = selectedDiscount === type ? "none" : type
      setSelectedDiscount(newDiscount)

      // Update form values
      form.setFieldValue("siblingDiscount", newDiscount === "sibling")
      form.setFieldValue("extraClassDiscount", newDiscount === "extraClass")

      const currentTuition = Number(
        tuitionOverride ?? feeBreakdown.tuitionTotal
      )
      const discountAmount =
        newDiscount !== "none" ? Math.ceil(currentTuition * 0.1) : 0
      form.setFieldValue("discountOverride", discountAmount)
    },
    [selectedDiscount, form, tuitionOverride, feeBreakdown.tuitionTotal]
  )

  // Handle waive supplies (toggle)
  const handleWaiveSupplies = useCallback(() => {
    const newWaive = !waiveSupplies
    setWaiveSupplies(newWaive)
    form.setFieldValue(
      "suppliesOverride",
      newWaive
        ? 0
        : selectedSuppliesFeeId
          ? SUPPLIES_FEES[selectedSuppliesFeeId].price
          : 0
    )
  }, [waiveSupplies, form, selectedSuppliesFeeId])

  const showSuppliesSection = hasSuppliesFee(classGroup)
  const showMaterialsSection = hasMaterialsFee(classGroup)

  return (
    <motion.div {...ANIMATION_BY_KEY.leftSm}>
      <Flex col gap={2}>
        {/* Tuition */}
        <SectionTitle>Tuition</SectionTitle>
        <LedgerRow>
          <Space size={8}>
            {hasMonthlyRate && (
              <SelectableButton
                $isSelected={!usingSemesterRate}
                $selectedColor={theme.activeColor}
                onClick={() => handleRateSelect("monthly")}
              >
                {`Monthly $${rates.monthly}`}
              </SelectableButton>
            )}
            {hasSemesterRate && (
              <DisabledFieldTooltip
                disabled={!canUseSemesterRate}
                tooltip={semesterRateDisabledReason}
              >
                <SelectableButton
                  $isSelected={usingSemesterRate}
                  $selectedColor={theme.activeColor}
                  onClick={() => handleRateSelect("semester")}
                  disabled={!canUseSemesterRate}
                >
                  {`Semester $${rates.semester}`}
                </SelectableButton>
              </DisabledFieldTooltip>
            )}
            <span>×{netSessionCount} new sessions</span>
          </Space>
          <Form.Item name="tuitionOverride" style={{ marginBottom: 0 }}>
            <StyledInput prefix="$" min={0} />
          </Form.Item>
        </LedgerRow>

        {/* Discounts */}
        <SectionTitle>Discount</SectionTitle>

        <LedgerRow>
          <Space size={8}>
            <SelectableButton
              $isSelected={selectedDiscount === "sibling"}
              $selectedColor={theme.activeColor}
              onClick={() => handleDiscountSelect("sibling")}
            >
              Sibling -10%
            </SelectableButton>
            <SelectableButton
              $isSelected={selectedDiscount === "extraClass"}
              $selectedColor={theme.activeColor}
              onClick={() => handleDiscountSelect("extraClass")}
            >
              Extra Class -10%
            </SelectableButton>
          </Space>
          <Form.Item name="discountOverride" style={{ marginBottom: 0 }}>
            <StyledInput
              prefix="-$"
              min={0}
              disabled={selectedDiscount === "none"}
            />
          </Form.Item>
        </LedgerRow>

        {/* Supplies */}
        {showSuppliesSection && (
          <>
            <Divider />

            <SectionTitle>Supplies</SectionTitle>
            <LedgerRow>
              <Space size={8}>
                {suppliesFeeOptions.map((fee) => {
                  const isSelected =
                    selectedSuppliesFeeId === fee.id && !waiveSupplies
                  return (
                    <SelectableButton
                      key={fee.id}
                      $isSelected={isSelected}
                      $selectedColor={theme.activeColor}
                      onClick={() =>
                        handleSuppliesFeeSelect(fee.id as SuppliesFeeId)
                      }
                    >
                      ${fee.price}{" "}
                      {SUPPLIES_FEE_LABELS[fee.id as SuppliesFeeId]}
                    </SelectableButton>
                  )
                })}
                <SelectableButton
                  $isSelected={waiveSupplies}
                  $selectedColor={theme.activeColor}
                  onClick={handleWaiveSupplies}
                >
                  Waive
                </SelectableButton>
              </Space>
              {!waiveSupplies && (
                <Form.Item name="suppliesOverride" style={{ marginBottom: 0 }}>
                  <StyledInput prefix="$" min={0} />
                </Form.Item>
              )}
            </LedgerRow>
          </>
        )}

        {/* Materials (Sculpture only) */}
        {showMaterialsSection && (
          <>
            <Divider />

            <SectionTitle>Materials</SectionTitle>
            <LedgerRow>
              <span>
                ${SCULPTURE_MATERIALS_FEE_PER_SESSION} × {netSessionCount} new
                sessions
              </span>
              <Form.Item name="materialsOverride" style={{ marginBottom: 0 }}>
                <StyledInput prefix="$" min={0} />
              </Form.Item>
            </LedgerRow>
          </>
        )}

        {/* Total */}
        <TotalSection $bgColor={theme.gray[1]}>
          <Flex col gap={2}>
            <TotalRow>
              <span>Tuition</span>
              <span>${finalBreakdown.tuitionTotal}</span>
            </TotalRow>

            {finalBreakdown.discountAmount > 0 && (
              <TotalRow>
                <span style={{ color: theme.success }}>Discount</span>
                <span style={{ color: theme.success }}>
                  -${finalBreakdown.discountAmount}
                </span>
              </TotalRow>
            )}

            {showSuppliesSection && (
              <TotalRow>
                <span>Supplies{waiveSupplies && " (waived)"}</span>
                <span>${finalBreakdown.suppliesFee}</span>
              </TotalRow>
            )}

            {showMaterialsSection && (
              <TotalRow>
                <span>Materials</span>
                <span>${finalBreakdown.materialsFee}</span>
              </TotalRow>
            )}

            <Divider />

            <TotalRow $isBold>
              <span>Total</span>
              <span>${finalBreakdown.grandTotal}</span>
            </TotalRow>
          </Flex>
        </TotalSection>
      </Flex>
    </motion.div>
  )
}

export default FeeSelector
